import React from "react";
import {
    Box,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
    Typography,
    Stack,
    Button,
    Fade,
} from "@mui/material";

export type TabAction = {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    variant?: "contained" | "outlined";
    disabled?: boolean;
};

export type ArtworkSupportingTab = {
    label: React.ReactNode;
    mobileLabel: string;
    component: React.ReactNode;
    hidden: boolean;
    actions?: TabAction[];
};

type ArtworkSupportingSectionProps = {
    isMobile: boolean;
    value: number;
    onTabChange: (_event: React.SyntheticEvent, newValue: number) => void;
    onSelectChange: (event: SelectChangeEvent<number>) => void;
    tabs: ArtworkSupportingTab[];
    title?: string;
};

type TabPanelProps = {
    children?: React.ReactNode;
    value: number;
    index: number;
};

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`artwork-supporting-tabpanel-${index}`}
            aria-labelledby={`artwork-supporting-tab-${index}`}
        >
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
};

const ArtworkSupportingSection: React.FC<ArtworkSupportingSectionProps> = ({
    isMobile,
    value,
    onTabChange,
    onSelectChange,
    tabs,
    title = "Supporting Information",
}) => {

    const tabsToShow = tabs.filter(tab => tab.hidden != true);
    const activeTab = tabsToShow[value];
    const showActions = (activeTab?.actions?.length ?? 0) > 0;

    return (
        <section className="artwork-section panels-section">
            <div className="section-header">
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        gap: 1.5,
                    }}
                >
                    <Typography variant="h3" sx={{ fontWeight: 500 }}>
                        {title}
                    </Typography>

                    <Box
                        sx={{
                            minHeight: { xs: showActions ? "auto" : 0, sm: 40 },
                            width: { xs: "100%", sm: "auto" },
                            display: "flex",
                            justifyContent: { xs: "stretch", sm: "flex-end" },
                        }}
                    >
                        <Fade in={showActions} timeout={180}>
                            <Box sx={{ width: "100%" }}>
                                {showActions && (
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        sx={{
                                            width: { xs: "100%", sm: "auto" },
                                        }}
                                    >
                                        {activeTab.actions?.map((action, index) => (
                                            <Button
                                                key={`${action.label}-${index}`}
                                                variant={action.variant ?? "contained"}
                                                startIcon={action.icon}
                                                onClick={action.onClick}
                                                disabled={action.disabled}
                                                size="small"
                                                fullWidth={isMobile}
                                            >
                                                {action.label}
                                            </Button>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        </Fade>
                    </Box>
                </Box>
            </div>

            <div className="section-content">
                <Box sx={{ width: "100%" }}>
                    {!isMobile ? (
                        <Tabs
                            value={value}
                            onChange={onTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                                borderRadius: "8px 8px 0 0",
                                minHeight: 48,
                                "& .MuiTab-root": {
                                    textTransform: "none",
                                    fontWeight: 500,
                                    minHeight: 48,
                                    color: "text.secondary",
                                    borderRight: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "rgba(0,0,0,0.02)",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: "rgba(0,0,0,0.05)",
                                    },
                                },
                                "& .Mui-selected": {
                                    color: "primary.main",
                                    fontWeight: 600,
                                    bgcolor: "background.default",
                                    borderBottom: "2px solid",
                                    borderColor: "primary.main",
                                },
                                "& .MuiTabs-indicator": {
                                    display: "none",
                                },
                            }}
                        >
                            {tabsToShow.map((tab, index) => (
                                <Tab
                                    key={index}
                                    id={`artwork-supporting-tab-${index}`}
                                    aria-controls={`artwork-supporting-tabpanel-${index}`}
                                    label={tab.label}
                                    disableRipple
                                    sx={{
                                        px: 3,
                                        py: 1.5,
                                        borderTopLeftRadius: "8px",
                                        borderTopRightRadius: "8px",
                                    }}
                                />
                            ))}
                        </Tabs>
                    ) : (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "grey.50",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        mb: 0.5,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        color: "text.secondary",
                                        textTransform: "uppercase"
                                    }}
                                >
                                    Section
                                </Typography>

                                <FormControl fullWidth size="small">
                                    <Select
                                        value={value}
                                        onChange={onSelectChange}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: { borderRadius: 2 }
                                            }
                                        }}
                                        sx={{
                                            bgcolor: "#0d057d",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#08034a",
                                                borderWidth: 2,
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "primary.dark",
                                                borderWidth: 2,
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "primary.dark",
                                                borderWidth: 2,
                                            },
                                            "& .MuiSvgIcon-root": {
                                                color: "primary.contrastText",
                                            },
                                            fontWeight: 500,
                                            color: "primary.contrastText",
                                        }}
                                    >
                                        {tabsToShow.map((tab, index) => (
                                            <MenuItem key={index} value={index}>
                                                {tab.mobileLabel}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                    )}

                    {tabsToShow.map((tab, index) => (
                        <TabPanel key={index} value={value} index={index}>
                            {tab.component}
                        </TabPanel>
                    ))}
                </Box>
            </div>
        </section>
    );
};

export default ArtworkSupportingSection;